import {
  AsyncActionButton,
  EditWorksheetDto,
  FilteredCreditApplication,
  Loader,
  PHONE_NUMBER_IFINANCE,
  PHONE_NUMBER_LINK_IFINANCE,
  reportErrorToConsole,
  useUpdateWorksheet,
} from '@applyonline/shared'
import { EFinancingProgram, EPaymentFrequency, EWorksheetStatus } from '@if/core/types'
import { formatCurrencyNoDecimals, formatCurrencyRoundedUpToNoDecimals } from '@if/core/utils'
import { FormEvent } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { getLoanAmount } from '../../data/creditapp-selectors'
import { useTrackStepReachedEvent } from '../../services/trackingEvents'
import { ETrackingEvent } from '../../types/tracking'
import useTermPaymentsTable from './hooks'

interface Props {
  creditApp: FilteredCreditApplication
}
const InitialApprobationForm = ({ creditApp }: Props) => {
  const { t } = useTranslation()
  const [monthlyPaymentPerMonths, isLoadingTermTable] = useTermPaymentsTable(creditApp)

  const [submitWorksheetUpdate, isSubmitting] = useUpdateWorksheet(EFinancingProgram.B2c)

  useTrackStepReachedEvent(creditApp, ETrackingEvent.QualifSuccess)

  const submitTerm = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (creditApp) {
      const term = monthlyPaymentPerMonths[monthlyPaymentPerMonths.length - 1].term

      const dto: EditWorksheetDto = {
        term,
        amountRequested: getLoanAmount(creditApp),
        creditApplicationId: creditApp.id,
        paymentFrequency: EPaymentFrequency.Monthly,
        deliveryOn: null,
        firstPaymentOn: null,
        includeInsurance: false,
        status: EWorksheetStatus.Draft,
        create: creditApp.worksheet === null,
        versionTag: creditApp.worksheet?.versionTag ?? null,
      }
      submitWorksheetUpdate(dto).catch(reportErrorToConsole)
    }
  }

  if (isLoadingTermTable || !creditApp)
    return <Loader title={t('common.withYouShortly')} description={t('common.pleaseWait')} />

  const requestedAmountReached = creditApp.requestedLoanAmount <= creditApp.finalDecision.maxAmountFinanced
  const loanAmount = Math.min(creditApp.requestedLoanAmount, creditApp.finalDecision.maxAmountFinanced)

  return (
    <main className="general-message" style={{ display: 'block' }}>
      {!monthlyPaymentPerMonths || monthlyPaymentPerMonths.length === 0 ? (
        <div id="loan-form-container">
          <div className="paragraph">
            <h3 style={{ textAlign: 'center', marginBottom: '2rem' }}>{t('decision.aprTooHighTitle')}</h3>
            <h4 style={{ textAlign: 'center', marginBottom: '2rem' }}>{t('decision.aprTooHighMessage')}</h4>
            <p style={{ textAlign: 'center' }}>
              <Trans
                i18nKey="loan.youHaveQuestions"
                values={{
                  phoneNumber: PHONE_NUMBER_IFINANCE,
                }}
                components={{ a: <Link to={PHONE_NUMBER_LINK_IFINANCE} style={{ textWrap: 'nowrap' }} /> }}
              />
            </p>
          </div>
        </div>
      ) : (
        <form onSubmit={submitTerm}>
          <div id="loan-form-container">
            <h3>
              {requestedAmountReached && (
                <>
                  <i className="fa-solid fa-party-horn" /> {t('common.congratulation')}{' '}
                </>
              )}
              {t('decision.hardhitDecisionConditionallyApprovedTitle')}
            </h3>
            <div className="paragraph">
              <p style={{ textAlign: 'center' }}>
                <Trans
                  i18nKey="decision.hardhitDecisionApproved"
                  values={{
                    maximumAmount: formatCurrencyNoDecimals(creditApp.finalDecision.maxAmountFinanced),
                    minPaymentAmount: formatCurrencyNoDecimals(
                      monthlyPaymentPerMonths[monthlyPaymentPerMonths.length - 1].amountWithoutInsurance,
                    ),
                  }}
                />
              </p>
            </div>
            <div>
              <div className="btn-group mobile-reversed justify-end" style={{ marginTop: '32px' }}>
                <AsyncActionButton type="submit" className="btn btn-blue" isPending={isSubmitting} primary>
                  <span>
                    {requestedAmountReached
                      ? t('common.imInterested')
                      : t('common.continueWith', { amount: formatCurrencyRoundedUpToNoDecimals(loanAmount) })}
                  </span>
                  <i className="fa-regular fa-arrow-right" />
                </AsyncActionButton>
              </div>
              <div className="paragraph">
                <p style={{ textAlign: 'center' }}>
                  <br />
                  <Trans
                    i18nKey="loan.youHaveQuestions"
                    values={{
                      phoneNumber: PHONE_NUMBER_IFINANCE,
                    }}
                    components={{ a: <Link to={PHONE_NUMBER_LINK_IFINANCE} style={{ textWrap: 'nowrap' }} /> }}
                  />
                </p>
              </div>
            </div>
            <div style={{ fontSize: '1.5rem', borderTop: '1px solid black', marginTop: '4rem', paddingTop: '2rem' }}>
              <sup style={{ color: 'red' }}>*</sup>
              &nbsp;
              {t('common.monthlyAmountsInclude', {
                financeFee: creditApp.normsDecision.lenderFeeRate,
                interestRate: creditApp.finalDecision.interestRate,
              })}
            </div>
          </div>
        </form>
      )}
    </main>
  )
}

export default InitialApprobationForm
